<template>
    <modal @close="onClose" ref="modal">
        <template v-slot:title>Confirm it's you</template>

        <slot>
            <p>For your security, confirm it's you before you change how you sign in.</p>

            <form-errors :errors="formErrors"/>

            <section class="border-top py-1" v-if="showPasskey" data-test="reverify-passkey">
                <section-toggle v-if="sections.length > 1" controls="reverifyPasskey" :open="isOpen('passkey')" @toggle="openSection = 'passkey'">
                    Use a passkey
                </section-toggle>
                <div id="reverifyPasskey" class="pt-1" v-show="isOpen('passkey')">
                    <p class="mb-1">Use your fingerprint, face, screen lock or security key.</p>
                </div>
            </section>

            <section class="border-top py-1" v-if="showCode" data-test="reverify-code">
                <section-toggle v-if="sections.length > 1" controls="reverifyCode" :open="isOpen('code')" @toggle="openSection = 'code'">
                    Use an authenticator app or backup code
                </section-toggle>
                <div id="reverifyCode" class="pt-1" v-show="isOpen('code')">
                    <form id="reverify-code-form" @submit.prevent="onSubmit" novalidate class="mui-form">
                        <div :class="{'has-danger': fieldErrors.length}">
                            <div class="mui-textfield mui-textfield-flipped mb-1">
                                <input id="reverify-code" name="mfa_code" type="text" maxlength="11" autocomplete="one-time-code" autocapitalize="off" spellcheck="false" v-model="formData.mfa_code" :class="{'mui--is-invalid': fieldErrors.length}">
                                <label for="reverify-code">Code from your authenticator app, or a backup code</label>
                            </div>
                            <div class="field-helper-text text-danger" v-if="fieldErrors.length">
                                <span class="sr-only">Error:</span>
                                <span> {{ fieldErrors[0] }}</span>
                            </div>
                        </div>
                    </form>
                </div>
            </section>

            <section class="border-top py-1" v-if="phoneOnly" data-test="reverify-sign-in-again">
                <section-toggle v-if="sections.length > 1" controls="reverifyPhone" :open="isOpen('phone')" @toggle="openSection = 'phone'">
                    Use your trusted phone
                </section-toggle>
                <div id="reverifyPhone" class="pt-1" v-show="isOpen('phone')">
                    <p class="mb-1">Sign in again, then approve the sign-in on your trusted phone.</p>
                </div>
            </section>

            <!-- One action at a time: the open section's -->
            <button-row>
                <button type="button" class="btn btn-outline-primary" data-test="reverify-cancel" @click.prevent="close">Cancel</button>
                <button v-if="isOpen('passkey')" type="button" class="btn btn-primary" :disabled="busy" @click="usePasskey">Use a passkey</button>
                <button v-if="isOpen('code')" type="submit" form="reverify-code-form" class="btn btn-primary" :disabled="busy">Confirm</button>
                <button v-if="isOpen('phone')" type="button" class="btn btn-primary" @click.prevent="reSignIn">Sign in again</button>
            </button-row>
        </slot>
    </modal>
</template>

<script>
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser'
import api from '@/api'
import ModalMixin from '@/mixins/ModalMixin'
import FormMixin from '@/mixins/Form'
import SectionToggle from '@/components/Login/SectionToggle'
import { signInAgain } from './mfaHelpers'

export default {
    mixins: [ModalMixin, FormMixin],
    components: {SectionToggle},
    props: {
        status: {type: Object, required: true},
    },
    data() {
        return {
            busy: false,
            openSection: '',
            resolver: null,
            rejecter: null,
            formRules: {
                mfa_code: {presence: {allowEmpty: false}},
            },
            formData: this.makeFormData(),
        }
    },
    computed: {
        fieldErrors() {
            return (this.errors && this.errors.mfa_code) || []
        },
        showPasskey() {
            return this.status.passkeys.length > 0 && browserSupportsWebAuthn()
        },
        showCode() {
            return this.status.totp_devices.length > 0 || this.status.backup_codes_left > 0
        },
        phoneOnly() {
            return this.status.totp_devices.length === 0 && this.status.passkeys.length === 0
        },
        sections() {
            return [
                this.showPasskey && 'passkey',
                this.showCode && 'code',
                this.phoneOnly && 'phone',
            ].filter(Boolean)
        },
    },
    methods: {
        isOpen(section) {
            return this.openSection == section
        },
        makeFormData() {
            return {mfa_code: ''}
        },
        // Resolves once verified, rejects ({cancelled}) if the user backs out
        ask() {
            return new Promise((resolve, reject) => {
                this.resolver = resolve
                this.rejecter = reject
                this.openSection = this.sections[0] || ''
                this.open()
            })
        },
        succeeded() {
            const resolve = this.resolver
            this.resolver = null
            this.rejecter = null
            if (resolve) {
                resolve()
            }
            this.close()
        },
        onClose() {
            const reject = this.rejecter
            this.resolver = null
            this.rejecter = null
            this.formData = this.makeFormData()
            if (reject) {
                reject({__all__: [], cancelled: true})
            }
            this.$emit('close')
        },
        onFailure(errors) {
            if (errors && errors.__status__ == 429) {
                this.formErrors = errors.__all__ || []
                return
            }
            this.errors = errors || {}
            this.formErrors = (errors && errors.__all__) || []
        },
        onSubmit() {
            if (!this.validate() || this.busy) {
                return
            }
            this.busy = true
            this.formErrors = []
            api.post('/account/mfa/verify', {mfa_code: this.formData.mfa_code}).then(() => {
                this.busy = false
                this.succeeded()
            }).catch((errors) => {
                this.busy = false
                this.onFailure(errors)
            })
        },
        usePasskey() {
            if (this.busy) {
                return
            }
            this.busy = true
            this.formErrors = []
            api.post('/account/mfa/verify/passkey/begin', {}).then((optionsJSON) => {
                return startAuthentication({optionsJSON})
            }).then((credential) => {
                return api.post('/account/mfa/verify/passkey/finish', {credential})
            }).then(() => {
                this.busy = false
                this.succeeded()
            }).catch((errors) => {
                this.busy = false
                if (errors && errors.__status__) {
                    this.onFailure(errors)
                } else {
                    this.formErrors = ["That passkey didn't work. Try again or use another method."]
                }
            })
        },
        reSignIn() {
            this.close()
            signInAgain(this)
        },
    },
}
</script>
